import React, {
    ChangeEvent,
    FocusEvent,
    FormEvent,
    KeyboardEvent,
    Ref,
    forwardRef,
    memo,
} from 'react';

import {Input as DSInput} from '@optimaxdev/design-system';

export type PropsType = {
    /**
     * The initial value of the control. This attribute is optional.
     */
    value?: string;
    /**
     * The name attribute specifies the name of the control.
     */
    name?: string;
    /**
     * A hint to the user of what can be entered in the control.
     */
    label?: string;
    /**
     * This shows the user that control contains some error data.
     */
    errorMessage?: React.ReactNode;
    /**
     * An icon that places at the control.
     */
    success?: boolean;
    /**
     * On blur event handler
     * @param {FocusEvent<HTMLInputElement>} event - On blur event
     */
    onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
    /**
     * On change event handler
     * @param {ChangeEvent<HTMLInputElement>} event - On change event
     */
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    /**
     * On key down event handler
     * @param {KeyboardEvent<HTMLInputElement>} event - On key down event
     */
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
    /**
     * Max length char
     */
    maxLength?: number;
    /**
     * Min length char
     */
    minLength?: number;
    /**
     * Oninvalid event if the <input> element is filled incorrectly.
     */
    onInvalid?: (event: FormEvent<HTMLInputElement>) => void;
    /**
     * Ref
     */
    ref?: Ref<HTMLInputElement>;
};

export const Input = memo(
    forwardRef<HTMLInputElement, PropsType>(
        (
            {
                errorMessage = '',
                label = '',
                name = '',
                success = false,
                value = '',
                ...restInputProps
            }: PropsType,
            ref,
        ) => {
            return (
                <DSInput
                    {...restInputProps}
                    isError={Boolean(errorMessage)}
                    isSuccess={success}
                    label={label}
                    message={errorMessage}
                    name={name}
                    ref={ref}
                    size="regular"
                    type="text"
                    value={value}
                />
            );
        },
    ),
);

Input.displayName = 'Input';
